import { faFacebookF, faLinkedinIn, faXTwitter } from '@fortawesome/free-brands-svg-icons';
import { faCopy } from '@fortawesome/pro-light-svg-icons';
import { faX } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Modal, ModalClose, ModalContent } from '@pickleballinc/react-ui';
import React, { useState } from 'react';

interface ShareModalProps {
	open: boolean;
	onOpenChange: () => void;
	title: string;
	tournamentInfo?: {
		name: string;
		location: string;
		dateRange: string;
		logoUrl: string;
	};
	url: string;
	slug?: string;
}

const ShareModal = ({ open, onOpenChange, title, tournamentInfo, url, slug }: ShareModalProps) => {
	const [copied, setCopied] = useState(false);

	const handleCopyLink = async () => {
		try {
			await navigator.clipboard.writeText(getShareableUrl());
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch (err) {
			console.error('Failed to copy:', err);
		}
	};

	const getShareableUrl = () => {
		const isDev = process.env.NEXT_PUBLIC_APP_ENV === 'dev';

		if (isDev && slug) {
			return `https://pickleballtournamentsdev.net/tournaments/${slug}`;
		}

		return url;
	};

	return (
		<Modal open={open} onOpenChange={onOpenChange}>
			<ModalContent className="!w-[540px] !p-6">
				<div className="relative mb-6">
					<h3 className="text-center text-xl font-semibold">Share This Tournament</h3>
					<ModalClose className="absolute right-0 top-0">
						<FontAwesomeIcon icon={faX} className="text-gray-400" />
					</ModalClose>
				</div>

				{tournamentInfo && (
					<div className="mb-6 flex items-center gap-4 rounded-lg border border-gray-200 p-4">
						<img src={tournamentInfo.logoUrl} alt={tournamentInfo.name} className="h-20 rounded-md object-contain" />
						<div className="flex-1">
							<h4 className="text-base font-semibold">{tournamentInfo.name}</h4>
							<p className="text-sm text-gray-600">{tournamentInfo.location}</p>
							<p className="text-sm text-green-600">{tournamentInfo.dateRange}</p>
						</div>
					</div>
				)}

				<div className="mb-6 flex justify-center gap-4">
					<a
						href={`https://www.facebook.com/sharer/sharer.php?u=${getShareableUrl()}`}
						target="_blank"
						rel="noopener noreferrer"
						className="flex h-10 w-10 items-center justify-center rounded-full bg-[#1877F2] text-white"
					>
						<FontAwesomeIcon icon={faFacebookF} />
					</a>
					<a
						href={`https://www.linkedin.com/sharing/share-offsite/?url=${getShareableUrl()}`}
						target="_blank"
						rel="noopener noreferrer"
						className="flex h-10 w-10 items-center justify-center rounded-full bg-[#1275B1] text-white"
					>
						<FontAwesomeIcon icon={faLinkedinIn} />
					</a>
					<a
						href={`https://twitter.com/intent/tweet?url=${getShareableUrl()}&text=${encodeURIComponent(title)}`}
						target="_blank"
						rel="noopener noreferrer"
						className="flex h-10 w-10 items-center justify-center rounded-full bg-[#f2f4f7] text-black"
					>
						<FontAwesomeIcon icon={faXTwitter} />
					</a>
				</div>

				<div className="flex items-center justify-center mb-4">
					<div className="h-px w-20 bg-gray-200" />
					<span className="px-4 text-sm text-gray-400">or</span>
					<div className="h-px w-20 bg-gray-200" />
				</div>

				<Button
					onClick={handleCopyLink}
					variant="secondary"
					className="flex w-full items-center justify-center gap-2 !border-gray-300 py-3 font-medium hover:!bg-gray-50"
				>
					<FontAwesomeIcon icon={faCopy} />
					<span>{copied ? 'Link copied' : 'Copy link'}</span>
				</Button>
			</ModalContent>
		</Modal>
	);
};

export default ShareModal;
